import React, { useState, useEffect, useCallback } from 'react';

import QuestionPage from '../../../templates/question';
import { RadioOptionsWithConfirm, TextareaWithConfirm } from 'shared-components';

import { Q } from '../../../../store/student/types';
import { InputFieldConfig, IImportData } from '../../../../types';
import { useDispatch } from 'react-redux';
import { savePI } from '../../../../store/student/actions';
import { createUpdatePIFieldAction } from '../../../../store/student/personalInfo/actions';
import { Pages } from '../../../../store/student/personalInfo/types';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';

export interface IProps {
  sectionTitle: string;
  complete: boolean;
  currentStep: number;
  steps: number;
  hasCriminalConvictions?: boolean;
  hasCriminalConvictionsVisible: boolean;
  hasCriminalConvictionsConfig: InputFieldConfig;
  criminalConvictionReason?: string;
  criminalConvictionsReasonVisible: boolean;
  criminalConvictionsReasonConfig: InputFieldConfig;
  config: any;
  isPageComplete?: boolean;
  importData: IImportData;
}

interface IState {
  hasCriminalConvictions?: boolean;
  importApproved: string[];
  importUpdateEnabled: string[];
  importEdited: string[];
}

const PersonalQPage4: React.FC<IProps> = props => {
  const dispatch = useDispatch();
  const onNext = (config: any) => {
    dispatch(savePI(Pages.PAGE4, config));
  };
  const onChange = (fieldName: string, value: boolean | undefined | string | number) =>
    dispatch(createUpdatePIFieldAction(fieldName, value));

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();

  const getImportApprovedFields = useCallback((): Array<Q> => {
    const importApproved = [];
    if (props.isPageComplete) {
      if (
        props.importData?.criminalConvictions !== undefined &&
        props.importData?.criminalConvictions === props.hasCriminalConvictions
      ) {
        importApproved.push(Q.HAS_CRIMINAL_CONVICTIONS);
      }
    }
    return importApproved;
  }, [props.hasCriminalConvictions, props.importData?.criminalConvictions, props.isPageComplete]);

  const getImportUpdateFields = useCallback((): Array<Q> => {
    const importUpdateEnabled = [];
    if (props.isPageComplete) {
      if (
        props.importData?.criminalConvictions !== undefined &&
        props.importData?.criminalConvictions !== props.hasCriminalConvictions
      ) {
        importUpdateEnabled.push(Q.HAS_CRIMINAL_CONVICTIONS);
      }
    }
    return importUpdateEnabled;
  }, [props.hasCriminalConvictions, props.importData?.criminalConvictions, props.isPageComplete]);

  const [state, setState] = useState<IState>({
    hasCriminalConvictions: props.hasCriminalConvictions,
    importApproved: [],
    importUpdateEnabled: [],
    importEdited: [],
  });

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      importApproved: getImportApprovedFields(),
      importUpdateEnabled: getImportUpdateFields(),
    }));
  }, [getImportApprovedFields, getImportUpdateFields]);

  const handleConvictionsClick = (question: any) => {
    onChange(Q.HAS_CRIMINAL_CONVICTIONS, question.char === 'Y');
    if (question.char === 'N') {
      onChange(Q.CRIMINAL_CONVICTIONS_REASON, '');
    }
  };

  const handleConvictionsInfoChange = (evt: React.FormEvent<HTMLInputElement>) => {
    onChange(Q.CRIMINAL_CONVICTIONS_REASON, evt.currentTarget.value);
  };

  const isImportEnabled = (target: Q): boolean => {
    switch (target) {
      case Q.HAS_CRIMINAL_CONVICTIONS:
        return props.hasCriminalConvictionsVisible && props.importData?.criminalConvictions !== undefined;
      default:
        return false;
    }
  };

  const isImportUpdateEnabled = (target: Q): boolean => {
    if (!props.isPageComplete || !props.complete) return false;
    switch (target) {
      case Q.HAS_CRIMINAL_CONVICTIONS:
        return props.hasCriminalConvictions !== undefined && state.importUpdateEnabled.includes(target);
      default:
        return false;
    }
  };

  const isImportComplete = (target: Q): boolean => {
    if (!isImportEnabled(target) || isImportUpdateEnabled(target)) return true;
    return state.importApproved.includes(target) || state.importEdited.includes(target);
  };

  const getComplete = (): boolean => {
    const importQuestions = [Q.HAS_CRIMINAL_CONVICTIONS];
    const importComplete = importQuestions.every(q => isImportComplete(q));
    return props.complete && importComplete;
  };

  const handleImportApprove = (target: Q): void => {
    setState(prevState => ({
      ...prevState,
      importApproved: [...prevState.importApproved, target],
      importEdited: prevState.importEdited.filter(q => q !== target),
    }));
  };

  const handleImportEdit = (target: Q): void => {
    setState(prevState => ({
      ...prevState,
      importApproved: prevState.importApproved.filter(q => q !== target),
      importEdited: [...prevState.importEdited, target],
    }));
  };

  const handleImportUndo = (target: Q): void => {
    setState(prevState => ({
      ...prevState,
      importApproved: prevState.importApproved.filter(q => q !== target),
      importEdited: prevState.importEdited.filter(q => q !== target),
    }));
  };

  return (
    <QuestionPage
      complete={getComplete()}
      currentStep={props.currentStep}
      steps={props.steps}
      onNext={() => onNext(props.config)}
      onBackToTaskList={onBackToTaskList}
      section={props.sectionTitle}
    >
      {props.hasCriminalConvictionsVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_CRIMINAL_CONVICTIONS}
          id={Q.HAS_CRIMINAL_CONVICTIONS}
          value={props.hasCriminalConvictions !== undefined ? (props.hasCriminalConvictions ? 'Yes' : 'No') : undefined}
          label={props.hasCriminalConvictionsConfig?.label}
          inputHelperText={props.hasCriminalConvictionsConfig?.helpText}
          onClick={(question: any) => handleConvictionsClick(question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: props.hasCriminalConvictions === true,
            },
            {
              char: 'N',
              label: 'No',
              selected: props.hasCriminalConvictions === false,
            },
          ]}
          importEnabled={isImportEnabled(Q.HAS_CRIMINAL_CONVICTIONS)}
          importValue={
            props.importData?.criminalConvictions !== undefined
              ? props.importData?.criminalConvictions
                ? 'Yes'
                : 'No'
              : undefined
          }
          importUpdateEnabled={isImportUpdateEnabled(Q.HAS_CRIMINAL_CONVICTIONS)}
          importApproved={state.importApproved.includes(Q.HAS_CRIMINAL_CONVICTIONS)}
          importEdited={state.importEdited.includes(Q.HAS_CRIMINAL_CONVICTIONS)}
          onImportApprove={() => {
            handleImportApprove(Q.HAS_CRIMINAL_CONVICTIONS);
            onChange(Q.HAS_CRIMINAL_CONVICTIONS, props.importData?.criminalConvictions);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.HAS_CRIMINAL_CONVICTIONS);
            onChange(
              Q.HAS_CRIMINAL_CONVICTIONS,
              isImportUpdateEnabled(Q.HAS_CRIMINAL_CONVICTIONS)
                ? props.hasCriminalConvictions
                : props.importData?.criminalConvictions,
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.HAS_CRIMINAL_CONVICTIONS);
            onChange(
              Q.HAS_CRIMINAL_CONVICTIONS,
              isImportUpdateEnabled(Q.HAS_CRIMINAL_CONVICTIONS) ? state.hasCriminalConvictions : undefined,
            );
          }}
        />
      )}
      {props.criminalConvictionsReasonVisible && (
        <TextareaWithConfirm
          id={Q.CRIMINAL_CONVICTIONS_REASON}
          value={props.criminalConvictionReason}
          label={props.criminalConvictionsReasonConfig?.label}
          inputHelperText={props.criminalConvictionsReasonConfig?.helpText}
          onChange={handleConvictionsInfoChange}
        />
      )}
    </QuestionPage>
  );
};

export default PersonalQPage4;
